/**
 * 認證管理工具模組
 * 
 * 負責管理用戶登入狀態、員工資訊等
 */

import { saveUserMd5, removeUserMd5, isLoggedIn } from './token';

const EMPLOYEE_INFO_KEY = 'employeeInfo';

/**
 * 保存登入後的員工資訊
 * @param {object} employeeData - 從登入接口返回的員工數據
 * @param {string} employeeData.Token - 用戶的 MD5 Token (32字元)
 * @param {string} employeeData.employeeId - 員工ID
 * @param {string} employeeData.name - 員工姓名
 * @param {string} employeeData.email - 員工郵箱
 * @param {string} employeeData.phone - 員工電話
 * @param {string} employeeData.status - 員工狀態
 */
export function saveEmployeeInfo(employeeData) {
    if (!employeeData || !employeeData.Token) {
        console.error('❌ 保存員工資訊失敗: 數據無效', employeeData);
        return false;
    }

    // 保存 userMd5
    const saved = saveUserMd5(employeeData.Token);

    if (!saved) {
        return false;
    }

    // 保存其他員工資訊（不包含 Token）
    const { Token, ...employeeInfo } = employeeData;
    localStorage.setItem(EMPLOYEE_INFO_KEY, JSON.stringify(employeeInfo));

    console.log('✅ 員工資訊已保存', employeeInfo);
    return true;
}

/**
 * 獲取保存的員工資訊
 * @returns {object|null} 員工資訊對象或 null
 */
export function getEmployeeInfo() {
    const infoStr = localStorage.getItem(EMPLOYEE_INFO_KEY);

    if (!infoStr) {
        return null;
    }

    try {
        const info = JSON.parse(infoStr);
        
        // 若 function 欄位為加密格式 (Base64.MD5)，則解碼還原為 JSON 字串
        // 原始 JSON 字串會以 '[' 開頭，而加密後的 Base64 通常不以 '[' 開頭
        if (info && info.function && typeof info.function === 'string' && info.function.includes('.') && !info.function.trim().startsWith('[')) {
            try {
                const [payloadBase64] = info.function.split('.');
                const jsonString = decodeURIComponent(escape(atob(payloadBase64)));
                info.function = jsonString; // 還原為原始的 JSON 字串，供各畫面繼續使用 JSON.parse 解析
            } catch (error) {
                console.error('❌ 解碼模組功能權限失敗:', error);
                info.function = '[]';
            }
        }
        
        return info;
    } catch (error) {
        console.error('❌ 解析員工資訊失敗:', error);
        return null;
    }
}

/**
 * 更新 localStorage 中部分員工資訊（不需要 Token）
 * 適用於後台修改當前登入者的角色/權限後，同步更新本地快取
 * @param {object} partialData - 要更新的欄位（例如 { role, function, permissions }）
 * @returns {boolean} 是否更新成功
 */
export function updateEmployeeInfo(partialData) {
    const current = getEmployeeInfo();
    if (!current) {
        console.warn('⚠️ 無法更新員工資訊：localStorage 中無現有資料');
        return false;
    }
    const updated = { ...current, ...partialData };
    localStorage.setItem(EMPLOYEE_INFO_KEY, JSON.stringify(updated));
    console.log('✅ 員工資訊已更新', updated);
    return true;
}

/**
 * 獲取當前登入的員工ID
 * @returns {string|null} 員工ID 或 null
 */
export function getEmployeeId() {
    const info = getEmployeeInfo();
    return info?.employeeId || null;
}

/**
 * 獲取當前登入的員工姓名
 * @returns {string|null} 員工姓名 或 null
 */
export function getEmployeeName() {
    const info = getEmployeeInfo();
    return info?.name || null;
}

/**
 * 清除所有登入資訊（登出時調用）
 */
export function logout() {
    // 清除 userMd5
    removeUserMd5();

    // 清除員工資訊
    localStorage.removeItem(EMPLOYEE_INFO_KEY);

    console.log('✅ 已登出，所有登入資訊已清除');
}

/**
 * 檢查用戶是否已登入
 * @returns {boolean} 是否已登入
 */
export function checkLogin() {
    return isLoggedIn();
}

/**
 * 解析角色資訊
 * 優先讀取明文 role 欄位（因為前端更新角色時只更新 role 欄位，permissions token 由服務端簽發不會即時更新）
 * 若 role 欄位不存在，則 fallback 解析 permissions token
 * @returns {object} 角色資訊 { isManager, isSales, isStaff }
 */
export function getRoles() {
    const info = getEmployeeInfo();

    // 優先：若 role 明文欄位有值，直接使用（前端更新角色後會同步此欄位）
    if (info?.role && typeof info.role === 'string' && info.role.trim() !== '') {
        const roles = info.role.split(',').map(r => r.trim()).filter(r => r);
        return {
            isManager: roles.includes('Manager'),
            isSales: roles.includes('Sales'),
            isStaff: roles.includes('Staff')
        };
    }

    // Fallback：解析 permissions token（服務端簽發，登入時存入）
    const permissions = info?.permissions;
    if (!permissions || typeof permissions !== 'string' || !permissions.includes('.')) {
        return { isManager: false, isSales: false, isStaff: false };
    }

    try {
        const [payloadBase64] = permissions.split('.');
        // 解碼 Base64
        const jsonString = decodeURIComponent(escape(atob(payloadBase64)));
        const permData = JSON.parse(jsonString);

        return {
            isManager: permData.m === 1,
            isSales: permData.s === 1,
            isStaff: permData.t === 1
        };
    } catch (error) {
        console.error('❌ 解析角色權限失敗:', error);
        return { isManager: false, isSales: false, isStaff: false };
    }
}

/**
 * 獲取登入狀態和用戶資訊
 * @returns {object} 登入狀態對象
 */
export function getAuthStatus() {
    return {
        isLoggedIn: isLoggedIn(),
        employeeInfo: getEmployeeInfo(),
        employeeId: getEmployeeId(),
        employeeName: getEmployeeName(),
        roles: getRoles()
    };
}

// 導出所有函數
export default {
    saveEmployeeInfo,
    updateEmployeeInfo,
    getEmployeeInfo,
    getEmployeeId,
    getEmployeeName,
    logout,
    checkLogin,
    getAuthStatus,
    getRoles
};
